<template>
  <div class="field">
    <div class="intent-picker">
      <h2>
        <strong>Intent</strong>
      </h2>
      <DropDownMenu dropDownIcon="none">
        <template #trigger>
          <button class="intent-picker__trigger button has-text-left">
            <div>
              <span v-if="selectedIntent">{{ selectedIntent.title }}</span>
              <span v-else class="intent-picker__trigger-placeholder">Select an intent</span>
            </div>
          </button>
        </template>
        <div>
          <div class="intent-picker__dropdown-item" v-for="intent in intentsForFaq" :key="intent.id">
            <a @click="selectIntent(intent)">
              <div class="intent-picker__intent-name">{{ intent.title }}</div>
            </a>
          </div>
          <hr class="dropdown-divider" />
          <div class="intent-picker__dropdown-item">
            <a class="intent-picker__add-intent" @click="showAddIntentModel = true">
              <AddIcon class="icon-1-3x"></AddIcon>
              Add new intent
            </a>
          </div>
        </div>
      </DropDownMenu>
    </div>
    <FaqAddIntentModal v-if="showAddIntentModel" @create="addedIntent" @close="showAddIntentModel = false" />
  </div>
</template>

<script lang="ts" setup>
import { IntentListItemViewModel } from '@/features/bots/models/intents/intentListItemViewModel';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import FaqAddIntentModal from '@/features/bots/components/designer/FaqAddIntentModal.vue';
import { computed, PropType, ref, watch } from 'vue';

const props = defineProps({
  intents: {
    type: Array as PropType<IntentListItemViewModel[]>,
    required: true,
  },
  faqName: {
    type: String,
    required: true,
  },
  intentId: {
    type: String,
  },
});

const emit = defineEmits<{
  (e: 'input', value: Partial<IntentListItemViewModel>): void;
}>();

const selectedIntent = ref<Partial<IntentListItemViewModel> | null>(null);
const showAddIntentModel = ref(false);

const intentsForFaq = computed(() =>
  props.intents
    .filter((intent: IntentListItemViewModel) => intent.id !== 'None')
    .sort((a: IntentListItemViewModel, b: IntentListItemViewModel) => a.title.localeCompare(b.title)),
);

watch(
  () => props.intentId,
  () => {
    if (selectedIntentIsNewIntent()) {
      return;
    }
    const matchingIntentOnId = props.intents.find((i) => i.id === props.intentId);
    if (matchingIntentOnId) {
      return selectIntent(matchingIntentOnId);
    }
    const matchingIntentOnName = props.intents.find((i) => i.title === props.faqName);
    if (matchingIntentOnName) {
      return selectIntent(matchingIntentOnName);
    }
    return selectIntent({ title: props.faqName });
  },
  { immediate: true },
);

function addedIntent(intentTitle: string) {
  const existingIntent = props.intents.find((i) => i.title === intentTitle);
  if (existingIntent) {
    selectIntent(existingIntent);
  } else {
    selectIntent({ title: intentTitle });
  }
}

function selectIntent(intent: Partial<IntentListItemViewModel>) {
  if (intent !== selectedIntent.value) {
    selectedIntent.value = intent;
    emitSelectedIntent();
  }
}

function selectedIntentIsNewIntent() {
  return (
    selectedIntent.value !== null &&
    selectedIntent.value?.id === undefined &&
    selectedIntent.value?.title !== props.faqName
  );
}

function emitSelectedIntent() {
  emit('input', selectedIntent.value!);
}
</script>

<style lang="scss" scoped>
.intent-picker {
  padding: 1rem 0.5rem;
  .dropdown {
    width: 100%;
    display: block;
  }
  :deep(.dropdown-menu) {
    width: 100%;
  }
  :deep(.dropdown-content) {
    padding: 0.5rem;
    max-height: 400px;
    overflow: auto;
  }
  &__trigger {
    display: block;
    padding-right: 2.5em;
    width: 100%;
    &::after {
      border: 3px solid transparent;
      border-radius: 2px;
      border-right: 0;
      border-top: 0;
      border-color: #485fc7;
      content: ' ';
      display: block;
      height: 0.625em;
      margin-top: -0.4375em;
      pointer-events: none;
      position: absolute;
      top: 50%;
      transform: rotate(-45deg);
      transform-origin: center;
      width: 0.625em;
      right: 1.125em;
      z-index: 4;
    }
    &:hover {
      &::after {
        border-color: #363636;
      }
    }
  }
  &__dropdown-item {
    display: block;
    width: 100%;
    font-size: 0.875rem;
    line-height: 1.5;
  }
  &__intent-name {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #212529;
    padding: 0.375rem 1rem;
    &:hover,
    &:focus {
      background-color: #f5f5f5;
      color: #0a0a0a;
    }
  }
  &__add-intent {
    display: block;
    padding: 0.375rem 1rem;
    color: $color-netcall-deep-sea;
    .icon-1-3x {
      position: relative;
      top: 2px;
      color: $color-netcall-deep-sea;
    }
    &:hover,
    &:focus {
      background-color: #f5f5f5;
      color: #0a0a0a;
    }
  }
  &__trigger-placeholder {
    color: #7d7d7d;
  }
}
</style>
